import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const CONSENT_KEY = 'analytics_consent';

function loadAnalytics() {
  // Microsoft Clarity
  if (!document.getElementById('clarity-script')) {
    const clarityScript = document.createElement('script');
    clarityScript.id = 'clarity-script';
    clarityScript.type = 'text/javascript';
    clarityScript.textContent = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "vkjom4036a");
    `;
    document.head.appendChild(clarityScript);
  }

  // Google Analytics
  if (!document.getElementById('ga-script')) {
    const gaLoader = document.createElement('script');
    gaLoader.id = 'ga-script';
    gaLoader.async = true;
    gaLoader.src = 'https://www.googletagmanager.com/gtag/js?id=G-MT3EQHLCC0';
    document.head.appendChild(gaLoader);

    const gaConfig = document.createElement('script');
    gaConfig.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-MT3EQHLCC0');
    `;
    document.head.appendChild(gaConfig);
  }

  // PostHog
  if (!document.getElementById('posthog-script')) {
    const phScript = document.createElement('script');
    phScript.id = 'posthog-script';
    phScript.textContent = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init("phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE",{api_host:"https://us.i.posthog.com",person_profiles:"identified_only"});
    `;
    document.head.appendChild(phScript);
  }

  // Meta (Facebook) Pixel — measures ad conversions (new-account signups). Loaded only on consent,
  // same as the analytics above. The CompleteRegistration event is fired from the signup flow (Auth.js).
  if (!document.getElementById('meta-pixel-script')) {
    const fbScript = document.createElement('script');
    fbScript.id = 'meta-pixel-script';
    fbScript.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '3644304639055926');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(fbScript);
  }
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent === 'accepted') {
      loadAnalytics();
    } else if (consent === null) {
      // Small delay to not distract from initial page load
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // While the bar is up, reserve its height at the bottom of the page so the last thing on any
  // page (e.g. a job's Apply Now button) can always be scrolled clear of it instead of sitting
  // underneath. Restored the moment the bar closes.
  useEffect(() => {
    if (!showBanner || !barRef.current) return undefined;
    const body = document.body;
    const previous = body.style.paddingBottom;
    const reserve = () => {
      const h = barRef.current ? barRef.current.getBoundingClientRect().height : 0;
      body.style.paddingBottom = `${Math.ceil(h) + 16}px`;
    };
    reserve();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(reserve);
      observer.observe(barRef.current);
    } else {
      window.addEventListener('resize', reserve);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', reserve);
      body.style.paddingBottom = previous;
    };
  }, [showBanner]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    loadAnalytics();
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  // Non-blocking bottom bar (same pattern as LanguageSelectionModal) so the visitor can keep
  // reading jobs while it sits there — no full-screen overlay. The X closes it as a Decline
  // (dismissing must never imply consent: no browser trackers load without an explicit Accept).
  return (
    <div ref={barRef} className="cookie-consent-bar fixed bottom-2 left-2 right-2 z-[100] md:left-4 md:right-auto md:bottom-4 md:w-96 animate-in slide-in-from-bottom duration-300">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            {t('cookieConsent.title', 'We use cookies & analytics')}
          </h3>
          <Button
            onClick={handleDecline}
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0 -mt-1 -mr-1"
            aria-label={t('cookieConsent.decline', 'Decline')}
          >
            <X size={14} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          {t('cookieConsent.message', 'We use analytics tools to monitor how the platform functions and catch technical bugs — this helps us keep everything running smoothly. No passwords or payment data are ever recorded.')}{' '}
          <Link to="/privacy" className="text-primary hover:underline font-medium">
            {t('cookieConsent.learnMore', 'Learn more in our Privacy Policy')}
          </Link>
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 rounded-lg" onClick={handleDecline}>
            {t('cookieConsent.decline', 'Decline')}
          </Button>
          <Button size="sm" className="flex-1 rounded-lg" onClick={handleAccept}>
            {t('cookieConsent.accept', 'Accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
